/**
 * A session's changed files, grouped by the branch they were written on
 * (RFC 0329 3c).
 *
 * Read off the record each write carries (3a): the branch its file landed on,
 * written down when the write came back. Nothing here asks where a file is;
 * a file with no record goes last, under "Elsewhere", and opens nothing.
 * Groups keep the order the files already have, most recently written first.
 */

import { codeFileId, worktreeRef } from '../../services/code/codeFile'
import type { FileChange } from './sessionChanges'

export interface ChangedFileGroup {
  /** The branch name, or null for "Elsewhere". */
  branch: string | null
  files: { change: FileChange; codeId: string | null }[]
}

export function groupChangedFiles(files: FileChange[]): ChangedFileGroup[] {
  const groups = new Map<string | null, ChangedFileGroup>()
  for (const change of files) {
    // A write on a detached checkout has a path but no branch to read it by.
    const branch = change.writtenOn?.branch ?? null
    const codeId =
      branch && change.writtenOn
        ? codeFileId(change.writtenOn.relPath, worktreeRef(branch))
        : null
    let group = groups.get(branch)
    if (!group) {
      group = { branch, files: [] }
      groups.set(branch, group)
    }
    group.files.push({ change, codeId })
  }
  const all = [...groups.values()]
  // Stable: branches in first-written order, Elsewhere last.
  return all.sort(
    (a, b) => Number(a.branch === null) - Number(b.branch === null),
  )
}
